import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { useTranslate } from '@tolgee/react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { PlusIcon } from '@heroicons/react/16/solid';
import classNames from 'classnames';
import useToggleOnlineOrderTag from '@client/page/management/online-order/detail/logic/use-toggle-online-order-tag.ts';
import {
  ONLINE_ORDER_TAG_DOT_COLOR,
  ONLINE_ORDER_TAG_LIST,
  resolveOnlineOrderTags,
} from './online-order-tags';
import OnlineOrderTagBadge from './online-order-tag-badge';

interface Props {
  orderID: string;
  tags: readonly string[];
}

// Detail-page tag editor: active tags show as removable badges and an
// "Add tag" button opens a menu of the tags not yet applied.
function OnlineOrderTagEditor(props: Props) {
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();
  const [toggleTag, { loading }] = useToggleOnlineOrderTag();

  const handleToggle = (tagKey: string) => {
    toggleTag({ variables: { id: props.orderID, tag: tagKey } }).catch((e: Error) => {
      showAlert({ status: 'error', title: t('common.error', 'Error'), message: e.message });
    });
  };

  const activeTags = resolveOnlineOrderTags(props.tags);
  const availableTags = ONLINE_ORDER_TAG_LIST.filter((tag) => !props.tags.includes(tag.key));

  return (
    <div className="flex flex-wrap items-center gap-2">
      {activeTags.map((tag) => (
        <OnlineOrderTagBadge
          key={tag.key}
          tag={tag}
          disabled={loading}
          onRemove={() => {
            handleToggle(tag.key);
          }}
        />
      ))}

      {availableTags.length > 0 && (
        <Menu>
          <MenuButton
            disabled={loading}
            className={classNames(
              'inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300',
              'px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors',
              'hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            {t('online-order.tags.add', 'Add tag')}
          </MenuButton>
          <MenuItems
            transition
            anchor="bottom start"
            className="z-10 rounded-xl border border-gray-200 bg-white p-1 shadow-md [--anchor-gap:4px] [--anchor-padding:8px]"
          >
            {availableTags.map((tag) => (
              <MenuItem key={tag.key}>
                <button
                  onClick={() => {
                    handleToggle(tag.key);
                  }}
                  className="flex w-full items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm hover:bg-gray-100"
                >
                  <span className={classNames('h-2 w-2 rounded-full', ONLINE_ORDER_TAG_DOT_COLOR[tag.color])} />
                  {tag.label}
                </button>
              </MenuItem>
            ))}
          </MenuItems>
        </Menu>
      )}
    </div>
  );
}

export default OnlineOrderTagEditor;
